import { Check, Clock } from 'lucide-react';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { APPROVAL_STATUS_TONE } from '@/styles/statusColors';
import { formatDisplayDate } from '@/utils/date';
import type { Batch } from '../types/publish.types';

const TRAIL_STEP_LABEL = {
  published: 'Published by',
  confirmed: 'Confirmed by',
  disbursed: 'Disbursed by',
} as const;

type TrailStepKey = keyof typeof TRAIL_STEP_LABEL;

const TRAIL_STEP_KEYS: readonly TrailStepKey[] = ['published', 'confirmed', 'disbursed'];

interface TrailStep {
  who: string | null;
  when: string | null;
  note: string | null;
}

function getTrailStep(batch: Batch, key: TrailStepKey): TrailStep {
  if (key === 'published') {
    return { who: batch.publishedBy, when: batch.publishDate, note: batch.publishComment };
  }
  if (key === 'confirmed') {
    return { who: batch.confirmedBy, when: batch.confirmDate, note: batch.salesComment };
  }
  return { who: batch.disbursedBy, when: batch.disburseDate, note: batch.accountsComment };
}

const isStepDone = (step: TrailStep | undefined): boolean =>
  step !== undefined && (step.who !== null || step.when !== null);

interface TrailStepDetailProps {
  step: TrailStep | undefined;
}

function TrailStepDetail({ step }: TrailStepDetailProps) {
  if (step === undefined) {
    return (
      <>
        <Skeleton className="mt-1.5 h-4 w-32" />
        <Skeleton className="mt-1.5 h-3 w-20" />
      </>
    );
  }
  if (!isStepDone(step)) {
    return <p className="mt-1 text-sm text-muted-foreground">Not yet</p>;
  }
  return (
    <>
      <p className="mt-1 text-sm font-semibold">{step.who ?? EMPTY_VALUE}</p>
      <p className="text-xs text-muted-foreground">{formatDisplayDate(step.when)}</p>
      {step.note === null ? null : (
        <ExpandableComment comment={step.note} className="mt-1 text-xs-plus" />
      )}
    </>
  );
}

interface TrailStepItemProps {
  stepKey: TrailStepKey;
  step: TrailStep | undefined;
}

function TrailStepItem({ stepKey, step }: TrailStepItemProps) {
  const label = TRAIL_STEP_LABEL[stepKey];
  const isDone = isStepDone(step);
  return (
    <li className="flex gap-3 px-4 py-3.5">
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          isDone ? ACCENT_MARK_CLASS : 'bg-card text-muted-foreground ring-1 ring-border',
        )}
        aria-hidden
      >
        {isDone ? <Check className="size-3.5" /> : <Clock className="size-3.5" />}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{label}</span>
          {step === undefined ? null : (
            <StatusBadge
              tone={isDone ? APPROVAL_STATUS_TONE.approved : APPROVAL_STATUS_TONE.pending}
            >
              {isDone ? 'Done' : 'Pending'}
            </StatusBadge>
          )}
        </div>
        <TrailStepDetail step={step} />
      </div>
    </li>
  );
}

interface BatchTrailProps {
  batch: Batch | undefined;
}

export function BatchTrail({ batch }: BatchTrailProps) {
  return (
    <ol className="grid grid-cols-1 divide-y divide-border rounded-xl bg-card shadow-panel ring-1 ring-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {TRAIL_STEP_KEYS.map((key) => (
        <TrailStepItem
          key={key}
          stepKey={key}
          step={batch === undefined ? undefined : getTrailStep(batch, key)}
        />
      ))}
    </ol>
  );
}
